import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Switch } from '@components/Switch';
import { Typography } from '@components/Typography';
import TeamSelect from '@features/TeamSelect';
import ChannelLocationSelect from '@features/ChannelLocationSelect';
import { useTranslation } from '@hooks';

const Container = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
        gridGap: '16px',
    },
    '& .team-select': {
        gridColumn: '1 / span 2',
    },
});

const providerOptions = [{ value: 0, label: 'SMTP' }];
const encryptionOptions = [
    { value: true, label: 'SSL' },
    { value: false, label: 'TLS' },
];

const EmailForm = ({ selectedChannel }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();

    const enableIncomingEmail = watch('enableIncomingEmail');

    return (
        <Container>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <Controller
                control={control}
                name="provider"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        onChange={({ target: { value } }) => {
                            setValue('provider', value, { shouldDirty: true });
                        }}
                        options={providerOptions}
                        label={t('SettingsChannels.provider')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        disabled
                    />
                )}
            />
            <TeamSelect data={selectedChannel} />
            <Controller
                control={control}
                name="smtpHost"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.smtpHost')}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                    />
                )}
            />
            <Controller
                control={control}
                name="smtpPort"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.smtpPort')}
                        type="number"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                    />
                )}
            />
            <Controller
                control={control}
                name="username"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.username')}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                    />
                )}
            />
            <Controller
                control={control}
                name="isSSL"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        onChange={({ target: { value } }) => {
                            setValue('isSSL', value, { shouldDirty: true });
                        }}
                        options={encryptionOptions}
                        label={t('SettingsChannels.encryption')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <Controller
                control={control}
                name="email"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('common.email')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <Controller
                control={control}
                name="password"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('common.password')}
                        type="password"
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <div style={{ gridColumn: '1 / span 2' }}>
                <Controller
                    control={control}
                    name="enableIncomingEmail"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={field.value}
                            handleChange={event => {
                                setValue('enableIncomingEmail', event.target.checked, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            label={t('SettingsChannels.enableIncomingEmail')}
                        />
                    )}
                />
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', display: 'block', marginTop: '-4px' }}
                >
                    {t('SettingsChannels.enableIncomingEmailHint')}
                </Typography>
            </div>
            {enableIncomingEmail && (
                <>
                    <Controller
                        control={control}
                        name="imapHost"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.imapHost')}
                                fullWidth
                                required
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="imapPort"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.imapPort')}
                                type="number"
                                fullWidth
                                required
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="imapUsername"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.imapUsername')}
                                placeholder={t('SettingsChannels.imapUsernamePlaceholder')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="imapPassword"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.imapPassword')}
                                placeholder={t('SettingsChannels.imapPasswordPlaceholder')}
                                type="password"
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="signature"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.signature')}
                                fullWidth
                                multiline
                                rows={3}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                sx={{ gridColumn: '1 / span 2' }}
                            />
                        )}
                    />
                </>
            )}
            <ChannelLocationSelect data={selectedChannel} />
        </Container>
    );
};

export default EmailForm;
